import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { CartDTO } from '@meshsmith/shared';
import { track } from '../lib/analytics';
import { useToast } from './useToast';

interface AddInput {
  variantId: string;
  quantity: number;
  note?: string;
  customOptions?: Record<string, string>;
}

interface CartCtx {
  cart: CartDTO | undefined;
  loading: boolean;
  add: (input: AddInput) => Promise<void>;
  update: (itemId: string, quantity: number) => Promise<void>;
  remove: (itemId: string) => Promise<void>;
  /** Applies a discount code. Rejects with the reason ("This code has expired.") when it cannot be used. */
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  itemCount: number;
}

const Ctx = createContext<CartCtx | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();

  const { data: cart, isLoading } = useQuery({
    queryKey: ['cart'],
    queryFn: () => api.get<CartDTO>('/cart'),
    staleTime: 15_000,
  });

  const invalidate = (next: CartDTO) => qc.setQueryData(['cart'], next);

  const addMut = useMutation({
    mutationFn: (input: AddInput) => api.post<CartDTO>('/cart/items', input),
    onSuccess: (next, input) => {
      invalidate(next);
      track('add_to_cart', { variantId: input.variantId, quantity: input.quantity });
      toast.push('Added to cart', 'success');
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const updateMut = useMutation({
    mutationFn: ({ itemId, quantity }: { itemId: string; quantity: number }) =>
      api.patch<CartDTO>(`/cart/items/${itemId}`, { quantity }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const removeMut = useMutation({
    mutationFn: (itemId: string) => api.del<CartDTO>(`/cart/items/${itemId}`),
    onSuccess: invalidate,
  });

  const applyCouponMut = useMutation({
    mutationFn: (code: string) => api.post<CartDTO>('/cart/coupon', { code }),
    onSuccess: (next) => {
      invalidate(next);
      track('coupon_apply', { code: next.coupon?.code });
    },
  });

  const removeCouponMut = useMutation({
    mutationFn: () => api.del<CartDTO>('/cart/coupon'),
    onSuccess: invalidate,
  });

  return (
    <Ctx.Provider
      value={{
        cart,
        loading: isLoading,
        itemCount: cart?.itemCount ?? 0,
        add: async (input) => {
          await addMut.mutateAsync(input);
        },
        update: async (itemId, quantity) => {
          await updateMut.mutateAsync({ itemId, quantity });
        },
        remove: async (itemId) => {
          await removeMut.mutateAsync(itemId);
        },
        applyCoupon: async (code) => {
          await applyCouponMut.mutateAsync(code);
        },
        removeCoupon: async () => {
          await removeCouponMut.mutateAsync();
        },
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useCart(): CartCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
